import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard, FolderKanban, FileSpreadsheet, Package, Building2,
    Users2, ClipboardList, Truck, FileText, ShieldCheck, Settings, LogOut, ScrollText, Moon, Sun,
} from "lucide-react";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
    { to: "/quotations", label: "Quotations", icon: FileSpreadsheet, testid: "nav-quotations" },
    { to: "/vendors", label: "Vendors", icon: Truck, testid: "nav-vendors" },
    { to: "/items", label: "Item catalog", icon: Package, testid: "nav-items" },
    { to: "/clients", label: "Clients", icon: Building2, testid: "nav-clients" },
    { to: "/orders", label: "Orders", icon: ClipboardList, testid: "nav-orders" },
    { to: "/client-quotations", label: "Client quotations", icon: FileText, testid: "nav-clientquotes" },
    { to: "/reports", label: "Reports", icon: ScrollText, testid: "nav-reports", perm: "report.view" },
    { to: "/users", label: "Users", icon: Users2, testid: "nav-users", adminOnly: true },
    { to: "/audit", label: "Audit log", icon: ShieldCheck, testid: "nav-audit", adminOnly: true },
    { to: "/settings", label: "Company settings", icon: Settings, testid: "nav-settings", adminOnly: true },
];

export default function Layout({ children }) {
    const { user, logout, isAdmin, hasPerm, dark, toggleDark } = useAuth();
    const nav = useNavigate();

    return (
        <div className="min-h-screen bg-stone-50 flex">
            {/* Sidebar */}
            <aside className="w-64 hidden md:flex flex-col bg-white border-r border-stone-200 h-screen sticky top-0">
                <div className="px-5 pt-6 pb-4 border-b border-stone-200 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 font-heading font-bold text-xl flex items-center justify-center">
                        E
                    </div>
                    <div>
                        <div className="font-heading font-semibold text-stone-900 leading-tight">Exprivia</div>
                        <div className="text-[11px] text-stone-500 uppercase tracking-wider">Purchase Portal</div>
                    </div>
                </div>
                <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                    {NAV.map((item) => {
                        if (item.adminOnly && !isAdmin()) return null;
                        if (item.perm && !hasPerm(item.perm) && !isAdmin()) return null;
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={item.testid}
                                className={({ isActive }) =>
                                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors " +
                                    (isActive
                                        ? "bg-amber-50 text-amber-800 border-l-4 border-amber-500 pl-2 font-medium"
                                        : "text-stone-600 hover:bg-stone-100")
                                }
                            >
                                <Icon size={16} strokeWidth={1.6} />
                                <span>{item.label}</span>
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="border-t border-stone-200 p-4">
                    <div className="text-sm font-medium text-stone-800 truncate">{user?.name}</div>
                    <div className="text-xs text-stone-500 truncate mb-3">{user?.email}</div>
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                            {user?.role?.replace("_", " ")}
                        </span>
                        <div className="flex items-center gap-3">
                            <button
                                data-testid="dark-mode-toggle"
                                onClick={toggleDark}
                                aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
                                title={dark ? "Switch to light mode" : "Switch to dark mode"}
                                className="text-stone-600 hover:text-amber-600 flex items-center"
                            >
                                {dark ? <Sun size={14} /> : <Moon size={14} />}
                            </button>
                            <button
                                data-testid="logout-btn"
                                onClick={async () => { await logout(); nav("/login"); }}
                                className="text-xs text-stone-600 hover:text-stone-900 flex items-center gap-1"
                            >
                                <LogOut size={13} /> Log out
                            </button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-amber-100 text-amber-700 font-bold flex items-center justify-center">E</div>
                    <div className="font-heading font-semibold text-stone-900">Exprivia</div>
                </div>
                <div className="flex items-center gap-3">
                    <button data-testid="mobile-dark-toggle" onClick={toggleDark} aria-label="Toggle dark mode" className="text-stone-600">
                        {dark ? <Sun size={16} /> : <Moon size={16} />}
                    </button>
                    <button data-testid="mobile-logout" onClick={async () => { await logout(); nav("/login"); }} className="text-sm text-stone-600">
                        <LogOut size={16} />
                    </button>
                </div>
            </div>

            {/* Main */}
            <main className="flex-1 md:pl-0 pt-16 md:pt-0">
                <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
                    {children}
                </div>
            </main>
        </div>
    );
}
